import {useEffect, useState, useCallback} from 'react';
import globalDomains from '@/utils/global-domains';

const DEFAULT_PROFILE_IMG = `${globalDomains.RES_AFREECATV}/images/svg/thumb_profile.svg`;

/**
 * Lazy load 이미지 관찰 훅
 * @param {Object} params - 설정 객체
 * @param {string} params.src - 로드할 이미지 소스
 * @param {'profile' | 'thumbnail'} params.type - 이미지 타입
 * @returns {Object} 이미지 src와 ref를 반환
 */
export function useLazyImageObserver({src, type, disableLazy}) {
  const [imageSrc, setImageSrc] = useState(src); // Lazy 로딩 이미지 상태
  const [imageElement, setImageElement] = useState(null); // 실제 DOM 요소를 추적할 상태
  const {protocol} = window.location;

  /**
   * IntersectionObserver 콜백 함수
   * @param {Array} entries - 관찰된 요소 배열
   * @param {IntersectionObserver} observer - 옵저버 객체
   */
  const loadImageOnIntersection = ([entry], observer) => {
    if (!entry.isIntersecting) {
      return;
    }

    if (src) {
      // 이미지 소스가 없는 경우 기본 이미지를 유지하고, 그렇지 않으면 로드할 소스를 설정
      const currentSrc = src?.includes('http') ? src : `${protocol}${src}`;
      setImageSrc(currentSrc); // 실제 이미지 로드
    } else {
      setImageSrc('');
    }

    observer.unobserve(entry.target); // 옵저버 중지
  };

  useEffect(() => {
    // 타입이 'profile'일 경우 기본 프로필 이미지 설정
    if (type === 'profile' && !disableLazy) {
      setImageSrc(DEFAULT_PROFILE_IMG);
    }

    if (!imageElement) {
      // 요소가 없으면 옵저버 등록 X
      return;
    }

    const observer = new IntersectionObserver(loadImageOnIntersection, {
      threshold: 0.1, // 10% 보일 때 동작
    });

    observer.observe(imageElement); // 요소 관찰 시작

    return () => observer.disconnect(); // 컴포넌트 언마운트 시 옵저버 해제
  }, [imageElement, src, type]);

  // ref가 DOM에 할당되면 imageElement를 설정
  const ref = useCallback((node) => {
    if (node) {
      setImageElement(node); // DOM 노드가 할당될 때 element 설정
    }
  }, []);

  return {imageRef: ref, imageSrc, setImageSrc};
}
